import { useState } from 'react'
import styled, { css } from 'styled-components/macro'
import { ifProp } from 'styled-tools'
import { Container, Divider, Drawer, List } from '@mui/material'
import { usePermission, usePermissionForAnyScope } from '@tomfxs/permissions-client'

import ApartmentIcon from '@mui/icons-material/Apartment'
import PersonIcon from '@mui/icons-material/Person'
import DashboardIcon from '@mui/icons-material/Dashboard'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import SchoolIcon from '@mui/icons-material/School'
import EventNoteIcon from '@mui/icons-material/EventNote'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import SettingsIcon from '@mui/icons-material/Settings'
import StorageIcon from '@mui/icons-material/Storage'
import LanIcon from '@mui/icons-material/Lan'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import MediationIcon from '@mui/icons-material/Mediation'
import GroupIcon from '@mui/icons-material/Group'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import StoreIcon from '@mui/icons-material/Store'
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SidebarHeader from './components/SidebarHeader'
import KeyIcon from '@mui/icons-material/Key'
import AssessmentIcon from '@mui/icons-material/Assessment'
import CertIcon from '@mui/icons-material/StarsOutlined'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TopicPoolIcon from '@mui/icons-material/AutoStories'
import AutoGraphIcon from '@mui/icons-material/AutoGraph'
import CertificateConfigurationIcon from '@mui/icons-material/Settings'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import CertificateIcon from '@mui/icons-material/AdminPanelSettings'
import CertificateAutomationIcon from '@mui/icons-material/AutoAwesome'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import RateReviewIcon from '@mui/icons-material/RateReview'
import QuizIcon from '@mui/icons-material/Quiz'
import { NavItem } from './components/NavItem'
import { useFlightDeck } from 'src/context/FlightDeckContext'

const DRAWER_WIDTH = '18em'

const drawerOpenCss = css`
    /* OPEN */
    width: ${DRAWER_WIDTH};
    transition: ${({ theme }) =>
        theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        })};
`

const drawerCloseCss = css`
    /* CLOSE */
    width: ${({ theme }) => theme.spacing(9)};
    overflow-x: hidden;
    transition: ${({ theme }) =>
        theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        })};
`

const StyledDrawer = styled(Drawer)<{ open: boolean }>`
    /* width: ${DRAWER_WIDTH}; */

    flex-shrink: 0;
    white-space: nowrap;
    display: flex;
    ${ifProp('open', drawerOpenCss, drawerCloseCss)}

    & .MuiPaper-root {
        padding-top: 64px;
        height: 100vh;

        display: flex;
        flex-direction: column;
        overflow: unset;

        ${ifProp('open', drawerOpenCss, drawerCloseCss)}
    }
`

const StyledContainer = styled(Container)`
    height: calc(100vh - 166px);
    overflow-y: auto;

    position: relative;

    display: flex;
    flex-direction: column;
`

const SidebarBodyList = styled(List)`
    flex: 1;
    z-index: 0;
`

const SidebarFooterList = styled(List)`
    width: 100%;
    background-color: #fff;
`

export function Sidebar() {
    const { company, academy } = useFlightDeck()
    const [open, setOpen] = useState(true)

    return (
        <StyledDrawer variant="permanent" open={open}>
            <SidebarHeader isSidebarOpen={open} toggleSidebar={() => setOpen(!open)} />
            <Divider />
            <StyledContainer disableGutters>
                <SidebarBodyList>
                    <NavItem title="Dashboard" isAllowed href="/" icon={<DashboardIcon />} />
                    <NavItem title="Stammdaten" isAllowed hideWithNoChildren icon={<StorageIcon />}>
                        <NavItem
                            title="Firmen"
                            isAllowed={usePermission('orga.company.view', {})}
                            href="/companies"
                            icon={<ApartmentIcon />}
                        />
                        <NavItem
                            title="Filialen"
                            isAllowed={usePermission('orga.companybranch.view', company ? { company: company._id } : {})}
                            href="/companyBranches"
                            icon={<StoreIcon />}
                        />
                        <NavItem
                            title="Abteilungen"
                            isAllowed={usePermission('orga.department.view', company ? { company: company._id } : {})}
                            href="/departments"
                            icon={<AccountTreeIcon />}
                        />
                        {<NavItem title="Anwender" isAllowed={usePermission('orga.user.view', {})} href="/users" icon={<PersonIcon />} />}
                    </NavItem>
                    <NavItem title="Traiwiss" isAllowed hideWithNoChildren icon={<AssignmentIcon />}>
                        {/* <NavItem title="Dashboard" isAllowed href="/traiwiss/events/dashboard" icon={<DashboardIcon />} /> */}
                        <NavItem title="Trainingskalender" isAllowed href="/events/calendar/participate" icon={<CalendarTodayIcon />} />
                        <NavItem
                            title="Akademien"
                            isAllowed={usePermission('traiwiss.academy.view', {})}
                            href="/academies"
                            icon={<SchoolIcon />}
                        />
                        <NavItem
                            title="Trainings"
                            isAllowed={usePermission('traiwiss.training.view', academy ? { academy: academy._id } : {})}
                            href="/trainings"
                            icon={<HeadsetMicIcon />}
                        />

                        <NavItem
                            title="Training Videos"
                            isAllowed={usePermission('traiwiss.training.view', academy ? { academy: academy._id } : {})}
                            href="/training-videos"
                            icon={<VideoLibraryIcon />}
                        />

                        <NavItem
                            title="Trainer"
                            isAllowed={usePermission('traiwiss.trainer.view', academy ? { academy: academy._id } : {})}
                            href="/trainers"
                            icon={<AssignmentIndIcon />}
                        />
                        <NavItem
                            title="Workflows"
                            isAllowed={usePermission('traiwiss.workflow.view', academy ? { academy: academy._id } : {})}
                            href="/workflows"
                            icon={<LanIcon />}
                        />
                        <NavItem
                            title="Workflowzuweisungen"
                            isAllowed={usePermission('traiwiss.participant.manage_workflows', academy ? { academy: academy._id } : {})}
                            href="/userWorkflowAssign"
                            icon={<MediationIcon />}
                        />
                        <NavItem
                            title="Teilnehmer"
                            isAllowed={usePermission('traiwiss.participant.view', academy ? { academy: academy._id } : {})}
                            href="/events/participants"
                            icon={<GroupIcon />}
                        />
                        <NavItem
                            title="Eventplanung"
                            isAllowed={usePermission('traiwiss.event.view', academy ? { academy: academy._id } : {})}
                            href="/events"
                            icon={<EventAvailableIcon />}>
                            <NavItem title="Liste" isAllowed href="/events/list" icon={<EventNoteIcon />} />
                            <NavItem title="Kalender" isAllowed href="/events/calendar/plan" icon={<CalendarTodayIcon />} />
                        </NavItem>
                    </NavItem>
                    <NavItem title="Zertifizierung" isAllowed hideWithNoChildren icon={<CertIcon />}>
                        <NavItem
                            title="Fragenpools"
                            isAllowed={usePermission('certification.topic.view', academy ? { academy: academy._id } : {})}
                            href="/certificate_topics"
                            icon={<TopicPoolIcon />}
                        />
                        <NavItem
                            title="Konfiguration"
                            isAllowed={usePermission(
                                'certification.certificateconfiguration.view',
                                academy ? { academy: academy._id } : {},
                            )}
                            href="/certificate_configurations"
                            icon={<CertificateConfigurationIcon />}
                        />
                        <NavItem
                            title="Zertifikate"
                            isAllowed={usePermission('certification.certificate.view', academy ? { academy: academy._id } : {})}
                            href="/certificates"
                            icon={<CertificateIcon />}
                        />
                        <NavItem
                            title="Automation"
                            isAllowed={usePermission('certification.hook.view', academy ? { academy: academy._id } : {})}
                            href="/certificate_automation"
                            icon={<CertificateAutomationIcon />}
                        />
                    </NavItem>
                    <NavItem title="Statistik" isAllowed hideWithNoChildren icon={<AssessmentIcon />}>
                        <NavItem
                            title="Suche"
                            isAllowed={usePermissionForAnyScope('statistic.statistic.view')}
                            href="/statistic/search"
                            icon={<QueryStatsIcon />}
                        />
                        <NavItem
                            title="Feedback"
                            isAllowed={usePermissionForAnyScope('statistic.feedback.view')}
                            href="/statistic/marah-feedbacks"
                            icon={<AutoGraphIcon />}
                        />
                    </NavItem>
                    <NavItem
                        title="Feedback"
                        isAllowed={usePermission('feedback.questionPool.view', {})}
                        hideWithNoChildren
                        icon={<ThumbUpIcon />}>
                        <NavItem
                            title="Fragenpools"
                            isAllowed={usePermission('feedback.questionPool.view', {})}
                            href="/marah-feedback/question_pools"
                            icon={<QuizIcon />}
                        />
                        <NavItem
                            title="Feedbacks"
                            isAllowed={usePermission('feedback.questionPool.view', {})}
                            href="/marah-feedback/feedbacks"
                            icon={<RateReviewIcon />}
                        />
                    </NavItem>
                </SidebarBodyList>
                <SidebarFooterList>
                    <NavItem title="Einstellungen" isAllowed hideWithNoChildren icon={<SettingsIcon />}>
                        <NavItem
                            isAllowed={usePermission('apigateway.apikey.view', {})}
                            title="Apikeys"
                            href="/settings/apikeys"
                            icon={<KeyIcon />}
                        />
                        <NavItem
                            title="Berechtigungen"
                            isAllowed={usePermission('general.manage_permissions', {})}
                            href="/settings/permissions"
                            icon={<ToggleOnIcon />}
                        />
                    </NavItem>
                </SidebarFooterList>
            </StyledContainer>
        </StyledDrawer>
    )
}
